// src/store/slices/appointmentSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { appointmentService } from "../../services/api/appointmentService";
import type { IAppointment } from "../../types/appointment";

interface AppointmentState {
  appointments: IAppointment[];
  selectedAppointment: IAppointment | null;
  loading: boolean;
  error: string | null;
}

const initialState: AppointmentState = {
  appointments: [],
  selectedAppointment: null,
  loading: false,
  error: null,
};

// Fetch appointments with search params
export const fetchAppointments = createAsyncThunk(
  "appointments/fetchAll",
  async (
    searchParams: {
      doctorIds?: string[];
      startDate?: string;
      endDate?: string;
      status?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const appointments = await appointmentService.getAppointmentSlots(
        searchParams
      );
      return appointments;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch appointments"
      );
    }
  }
);

// Book appointment
export const bookAppointment = createAsyncThunk(
  "appointments/book",
  async (
    bookingData: {
      appointmentId: string;
      doctorId: string;
      patientName: string;
      patientPhone: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const appointment = await appointmentService.bookPatientAppointment(
        bookingData.appointmentId,
        bookingData.doctorId,
        {
          patientName: bookingData.patientName,
          patientPhone: bookingData.patientPhone,
        }
      );
      return appointment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to book appointment"
      );
    }
  }
);

// Generate slots
export const generateAppointmentSlots = createAsyncThunk(
  "appointments/generateSlots",
  async (
    params: {
      doctorId: string;
      startDate: string;
      endDate: string;
      startTime: string;
      endTime: string;
      slotDuration: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const slots = await appointmentService.generateAppointmentSlots(params);
      return slots;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to generate slots"
      );
    }
  }
);

// Update appointment status
export const updateAppointmentStatus = createAsyncThunk(
  "appointments/updateStatus",
  async (
    params: {
      appointmentId: string;
      fromStatus: string;
      toStatus: string;
      patientDetails?: {
        patientName: string;
        patientPhone: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const success = await appointmentService.updateAppointmentStatus(
        params.appointmentId,
        params.fromStatus,
        params.toStatus,
        params.patientDetails
      );
      return { id: params.appointmentId, success };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update appointment status"
      );
    }
  }
);

// Get single appointment
export const fetchAppointmentById = createAsyncThunk(
  "appointments/fetchOne",
  async (appointmentId: string, { rejectWithValue }) => {
    try {
      const appointment = await appointmentService.getAppointmentById(
        appointmentId
      );
      return appointment;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch appointment"
      );
    }
  }
);

const appointmentSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch appointments
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Book appointment
      .addCase(bookAppointment.fulfilled, (state, action) => {
        // Convert IAppointmentResponse to IAppointment
        const appointmentData: IAppointment = {
          _id: action.payload.id, // Convert id to _id
          clinicId: action.payload.clinicId,
          doctorId: action.payload.doctorId,
          patientName: action.payload.patientName,
          patientPhone: action.payload.patientPhone,
          scheduledStartTime: action.payload.scheduledStartTime,
          scheduledEndTime: action.payload.scheduledEndTime,
          actualStartTime: action.payload.actualStartTime,
          actualEndTime: action.payload.actualEndTime,
          status: action.payload.status,
          createdAt: action.payload.createdAt,
          updatedAt: action.payload.updatedAt,
        };
        const index = state.appointments.findIndex(
          (apt) => apt._id === action.payload.id
        );
        if (index !== -1) {
          state.appointments[index] = appointmentData;
        }
      })
      // Fetch single appointment
      .addCase(fetchAppointmentById.fulfilled, (state, action) => {
        state.selectedAppointment = action.payload;
      })
      // Update status
      .addCase(updateAppointmentStatus.fulfilled, (state) => {
        /*if (action.payload.success) {
          const index = state.appointments.findIndex(
            (apt) => apt._id === action.payload.id
          );
          if (index !== -1) {
            // Refresh the appointments list after status update
            // You might want to fetch the updated appointment here
          }
        }*/
        state.loading = false;
        state.error = null;
        state.selectedAppointment = null;
      });
  },
});

export default appointmentSlice.reducer;
